import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import { Button, Card, StatusPill, Table, EmptyState, Input, Select, useToast } from '../../design-system';
import DscModal from '../components/DscModal';
import DscCustodyModal from '../components/DscCustodyModal';
import ImportModal from '../components/ImportModal';
import DscScheduleModal from '../components/DscScheduleModal';
import {
  dscStatus, validityText, fmtDate,
  STATUS_VARIANT, STATUS_LABEL, STATUS_FILTERS,
  HOLDER_LABEL, HOLDER_VARIANT, custodyText,
  compareDscCode, toCsv,
} from '../../lib/dsc';
import '../styles/Dsc.css';

const downloadCsv = (rows, filename) => {
  const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

function OrgAdminDSC() {
  const toast = useToast();
  const [importOpen, setImportOpen] = useState(false);

  // ⚠️ `?settings=notifications` OPENS THE SCHEDULE DIALOG ON ARRIVAL, and the
  // Send Notifications page links straight to it (lib/notificationSettings.js).
  // Landing somebody on the register instead, with the thing they came for
  // still behind a toolbar button, makes that link a hint rather than a
  // destination.
  const [searchParams, setSearchParams] = useSearchParams();
  const [scheduleOpen, setScheduleOpen] = useState(
    () => searchParams.get('settings') === 'notifications'
  );

  // Spent the moment it is honoured. Left in the URL, closing the dialog and
  // refreshing — or arriving back here with the Back button — reopens it, which
  // looks like a dialog that cannot be dismissed.
  useEffect(() => {
    if (searchParams.get('settings') !== 'notifications') return;
    const next = new URLSearchParams(searchParams);
    next.delete('settings');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [dscs, setDscs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [modal, setModal] = useState(null); // null | { dsc } (dsc null = create)
  const [custodyFor, setCustodyFor] = useState(null); // DSC whose custody modal is open
  const [view, setView] = useState('active'); // 'active' | 'deleted'
  const deletedView = view === 'deleted';

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/dscs', { params: deletedView ? { deleted: true } : {} });
      setDscs(res.data);
      setSelected(new Set());
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load DSCs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const stats = useMemo(() => {
    const s = { total: dscs.length, active: 0, expiring: 0, expired: 0 };
    dscs.forEach((d) => {
      const st = dscStatus(d.validTo);
      if (st === 'active') s.active += 1;
      else if (st === 'expiring') s.expiring += 1;
      else if (st === 'expired') s.expired += 1;
    });
    return s;
  }, [dscs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return dscs
      .filter((d) => {
        if (statusFilter && dscStatus(d.validTo) !== statusFilter) return false;
        if (!q) return true;
        return [d.dscCode, d.name, d.clientId?.displayName, d.pan, d.tokenSerial, d.associatedPerson, d.place, d.remarks, d.currentHolderName]
          .some((v) => (v || '').toLowerCase().includes(q));
      })
      .sort((a, b) => compareDscCode(a.dscCode, b.dscCode));
  }, [dscs, search, statusFilter]);

  const allVisibleSelected = filtered.length > 0 && filtered.every((d) => selected.has(d._id));

  const toggleOne = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected((prev) => {
      if (allVisibleSelected) return new Set();
      const next = new Set(prev);
      filtered.forEach((d) => next.add(d._id));
      return next;
    });

  const remove = async (dsc) => {
    if (!window.confirm(`Delete DSC "${dsc.dscCode}"? You can restore it later from the Deleted view.`)) return;
    try {
      await apiClient.delete(`/api/dscs/${dsc._id}`);
      toast.success('DSC deleted');
      load();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to delete DSC');
    }
  };

  const restore = async (dsc) => {
    try {
      await apiClient.patch(`/api/dscs/${dsc._id}/restore`);
      toast.success('DSC restored');
      load();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to restore DSC');
    }
  };

  const bulkDelete = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected DSC${ids.length > 1 ? 's' : ''}?`)) return;
    try {
      const res = await apiClient.post('/api/dscs/bulk-delete', { ids });
      toast.success(`Deleted ${res.data.deleted} DSC${res.data.deleted === 1 ? '' : 's'}`);
      load();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to delete');
    }
  };

  const exportRows = (rows, name) => {
    if (rows.length === 0) return toast.danger('Nothing to export');
    // Flatten the populated client and the custody summary into CSV columns.
    downloadCsv(
      rows.map((d) => ({ ...d, clientName: d.clientId?.displayName || '', custody: custodyText(d) })),
      name
    );
  };

  // Importing moved to the shared .xlsx flow (ImportModal + /api/dscs/import).
  // The CSV path it replaced parsed the file in the browser and could only
  // report "3 added, 1 failed" — no template, no dropdowns, and no way to learn
  // WHICH row failed or why. Export stays CSV: reading a register into Excel
  // has no such problem.

  const columns = [
    // Bulk-select only makes sense in the active view.
    ...(deletedView
      ? []
      : [
          {
            key: 'select',
            width: 40,
            header: (
              <input
                type="checkbox"
                aria-label="Select all"
                checked={allVisibleSelected}
                onChange={toggleAll}
              />
            ),
            render: (d) => (
              <input
                type="checkbox"
                aria-label={`Select ${d.dscCode}`}
                checked={selected.has(d._id)}
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggleOne(d._id)}
              />
            ),
          },
        ]),
    { key: 'dscCode', header: 'DSC Code', render: (d) => <span className="dsc-code">{d.dscCode}</span> },
    { key: 'name', header: 'Name', render: (d) => d.name || '—' },
    {
      key: 'client',
      header: 'Client',
      render: (d) => d.clientId?.displayName || <span className="dsc-muted">Firm's own</span>,
    },
    { key: 'pan', header: 'PAN', render: (d) => d.pan || '—' },
    { key: 'tokenSerial', header: 'Token', render: (d) => d.tokenSerial || '—' },
    {
      key: 'validity',
      header: 'Validity',
      render: (d) => {
        const st = dscStatus(d.validTo);
        return (
          <div className="dsc-validity">
            <StatusPill variant={STATUS_VARIANT[st]} label={STATUS_LABEL[st]} />
            <span className="dsc-validity-sub">
              {fmtDate(d.validTo)}
              {d.validTo && <em> · {validityText(d.validTo)}</em>}
            </span>
          </div>
        );
      },
    },
    {
      key: 'custody',
      header: 'Custody',
      render: (d) => (
        <div className="dsc-validity">
          <StatusPill variant={HOLDER_VARIANT[d.currentHolder] || 'success'} label={HOLDER_LABEL[d.currentHolder] || 'With firm'} />
          {d.currentHolderName && (
            <span className="dsc-validity-sub">
              {d.currentHolderName}
              {d.custodySince && <em> · since {fmtDate(d.custodySince)}</em>}
            </span>
          )}
        </div>
      ),
    },
    { key: 'place', header: 'Place', render: (d) => d.place || '—' },
    { key: 'associatedPerson', header: 'Held by', render: (d) => d.associatedPerson || '—' },
    {
      key: 'actions',
      width: deletedView ? 100 : 190,
      header: '',
      render: (d) =>
        deletedView ? (
          <div className="dsc-row-actions" onClick={(e) => e.stopPropagation()}>
            <Button size="sm" variant="secondary" onClick={() => restore(d)}>Restore</Button>
          </div>
        ) : (
          <div className="dsc-row-actions" onClick={(e) => e.stopPropagation()}>
            <Button size="sm" variant="secondary" onClick={() => setCustodyFor(d)}>Custody</Button>
            <Button size="sm" variant="secondary" onClick={() => setModal({ dsc: d })}>Edit</Button>
            <Button size="sm" variant="ghost" onClick={() => remove(d)}>Delete</Button>
          </div>
        ),
    },
  ];

  // Clicking a card filters the table by that status; the Total card clears it.
  const statCards = [
    { label: 'Total', value: stats.total, tone: '', filter: '' },
    { label: 'Active', value: stats.active, tone: 'success', filter: 'active' },
    { label: 'Expiring ≤ 30 days', value: stats.expiring, tone: 'warning', filter: 'expiring' },
    { label: 'Expired', value: stats.expired, tone: 'danger', filter: 'expired' },
  ];

  return (
    <OrgAdminLayout title="DSC" subtitle="Digital Signature Certificates — tokens, holders and validity">
      <div className="dsc-page">
        {!deletedView && (
          <div className="dsc-stats">
            {statCards.map((c) => (
              <Card
                key={c.label}
                className={`dsc-stat dsc-stat-clickable${c.tone ? ` ${c.tone}` : ''}${statusFilter === c.filter ? ' active' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => setStatusFilter(c.filter)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setStatusFilter(c.filter); }
                }}
              >
                <div className="dsc-stat-num">{c.value}</div>
                <div className="dsc-stat-label">{c.label}</div>
              </Card>
            ))}
          </div>
        )}

        <div className="dsc-view-toggle">
          <Button size="sm" variant={deletedView ? 'secondary' : 'primary'} onClick={() => setView('active')}>Active</Button>
          <Button size="sm" variant={deletedView ? 'primary' : 'secondary'} onClick={() => setView('deleted')}>Deleted</Button>
        </div>

        <Card className="dsc-toolbar-card">
          <div className="dsc-toolbar">
            <Input
              aria-label="Search DSCs"
              placeholder="Search code, name, PAN, token, place…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Select aria-label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              {STATUS_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </Select>
            <div className="dsc-toolbar-actions">
              {/* Beside Import/Export rather than hidden in a settings page:
                  the person who notices the register is drifting is the person
                  looking at it, and that is the moment to say who gets told. */}
              {!deletedView && (
                <Button size="sm" variant="secondary" onClick={() => setScheduleOpen(true)}>
                  Notifications
                </Button>
              )}
              {!deletedView && <Button size="sm" variant="secondary" onClick={() => setImportOpen(true)}>Import</Button>}
              <Button size="sm" variant="secondary" onClick={() => exportRows(filtered, deletedView ? 'dscs-deleted.csv' : 'dscs.csv')}>Export</Button>
              {!deletedView && <Button variant="primary" onClick={() => setModal({ dsc: null })}>+ Add DSC</Button>}
            </div>
          </div>
          {!deletedView && selected.size > 0 && (
            <div className="dsc-bulk-bar">
              <span>{selected.size} selected</span>
              <Button size="sm" variant="secondary" onClick={() => exportRows(filtered.filter((d) => selected.has(d._id)), 'dscs-selected.csv')}>
                Export selected
              </Button>
              <Button size="sm" variant="danger" onClick={bulkDelete}>Delete selected</Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
            </div>
          )}
        </Card>

        {!loading && dscs.length === 0 ? (
          deletedView ? (
            <EmptyState icon="🗑️" title="Nothing deleted" description="Deleted DSCs will appear here and can be restored." />
          ) : (
            <EmptyState icon="🔑" title="No DSCs yet" description="Add a Digital Signature Certificate to start tracking its token and validity." />
          )
        ) : !loading && filtered.length === 0 ? (
          <EmptyState icon="🔍" title="No matches" description="No DSCs match your search or filter." />
        ) : (
          <Card>
            <Table columns={columns} data={filtered} loading={loading} rowClassName={(d) => (selected.has(d._id) ? 'dsc-row-selected' : '')} />
          </Card>
        )}
      </div>

      {modal && (
        <DscModal
          dsc={modal.dsc}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
      {custodyFor && (
        <DscCustodyModal
          dsc={custodyFor}
          onClose={() => setCustodyFor(null)}
          onChanged={load}
        />
      )}
      <ImportModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={load}
        basePath="/api/dscs"
        title="Import DSCs from a spreadsheet"
        noun="DSC"
        nounPlural="DSCs"
        templateFile="dsc-import-template.xlsx"
        describeTemplate="An Excel file with the columns we accept, a dropdown-free date format spelled out, and a sheet listing your clients. One row = one DSC token."
        describeUpload="Leave DSC Code blank to have one assigned automatically. A row whose code already exists updates that DSC instead of creating a second one — so you can Export, correct in Excel, and import back."
        rowLabel={(r) => r.dscCode}
      />
      {scheduleOpen && <DscScheduleModal onClose={() => setScheduleOpen(false)} />}
    </OrgAdminLayout>
  );
}

export default OrgAdminDSC;
